import React, { useState, useEffect } from 'react';

interface CreateZoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ZoneFormData) => void;
  pieces: Array<{ id: string; code: string; nom: string }>;
  editingZone?: any;
}

export interface ZoneFormData {
  nom: string;
  code: string;
  type: string;
  codeBarres?: string;
  nombreEtageres?: number;
  theme?: string;
  pieceId: string;
}

const TYPES_ZONE = [
  'Armoire',
  'Étagère',
  'Bureau',
  'Placard',
  'Meuble',
  'Rack',
  'Casier',
  'Autre'
];

const THEMES = [
  'PEDAGOGIE',
  'JEUX_EXTERIEURS',
  'MATERIEL_CREATIF',
  'MUSIQUE_SON',
  'BUREAUTIQUE',
  'ACTIVITES_NATURE',
  'SECURITE',
  'DIVERS'
];

const CreateZoneModal: React.FC<CreateZoneModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  pieces,
  editingZone
}) => {
  const [formData, setFormData] = useState<ZoneFormData>({
    nom: '',
    code: '',
    type: 'Armoire',
    codeBarres: '',
    nombreEtageres: 1,
    theme: '',
    pieceId: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (editingZone) {
      setFormData({
        nom: editingZone.nom || '',
        code: editingZone.code || '',
        type: editingZone.type || 'Armoire',
        codeBarres: editingZone.codeBarres || '',
        nombreEtageres: editingZone.nombreEtageres || 1,
        theme: editingZone.theme || '',
        pieceId: editingZone.pieceId || '',
      });
    } else {
      setFormData({
        nom: '',
        code: '',
        type: 'Armoire',
        codeBarres: '',
        nombreEtageres: 1,
        theme: '',
        pieceId: '',
      });
    }
  }, [editingZone, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.code.trim()) newErrors.code = 'Le code est obligatoire';
    if (!formData.pieceId) newErrors.pieceId = 'La pièce est obligatoire';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      setFormData({ nom: '', code: '', type: 'Armoire', codeBarres: '', nombreEtageres: 1, theme: '', pieceId: '' });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  🏠 {editingZone ? 'Modifier la Zone' : 'Nouvelle Zone'}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pièce */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pièce <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="pieceId"
                    value={formData.pieceId}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.pieceId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Sélectionner une pièce</option>
                    {pieces.map(piece => (
                      <option key={piece.id} value={piece.id}>
                        {piece.nom} ({piece.code})
                      </option>
                    ))}
                  </select>
                  {errors.pieceId && <p className="mt-1 text-sm text-red-600">{errors.pieceId}</p>}
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {TYPES_ZONE.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nom ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Armoire A, Étagère principale"
                  />
                  {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: ROB2-AR-A01"
                    maxLength={50}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                </div>

                {/* Code-barres */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code-barres
                  </label>
                  <input
                    type="text"
                    name="codeBarres"
                    value={formData.codeBarres}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 123456789"
                  />
                </div>

                {/* Nombre d'étagères */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre d'étagères
                  </label>
                  <input
                    type="number"
                    name="nombreEtageres"
                    value={formData.nombreEtageres}
                    onChange={handleChange}
                    min="1"
                    max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Thème */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thème
                  </label>
                  <select
                    name="theme"
                    value={formData.theme}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucun (attribution automatique)</option>
                    {THEMES.map(theme => (
                      <option key={theme} value={theme}>{theme.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Laissez vide pour attribution automatique selon les objets contenus
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {editingZone ? 'Modifier la zone' : 'Créer la zone'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateZoneModal;

